import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms'; 
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { MatTooltip } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { RefreshService } from 'src/app/refresh.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { ApiService } from 'src/app/services/api.service';

// Constants
import { Urls } from 'src/app/lib/urls';
import { TRANSACTION_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { REDEEM_TABLE_SUB_HEADERS } from 'src/app/lib/constants/transactions.constants';

// Modules
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { SharedPipeModule } from 'src/app/shared/modules/shared-pipe.module';
import { PaginationComponent } from 'src/app/shared/component/pagination/pagination.component';
import { TableControlsComponent } from 'src/app/shared/component/table-controls/table-controls.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { DropdownFieldComponent } from 'src/app/shared/component/custom-dropdown/custom-dropdown.component';
import { DateInputComponent } from 'src/app/shared/component/date-input/date-input.component';

@Component({
  selector: 'app-redeem',
  templateUrl: './redeem.component.html',
  styleUrl: './redeem.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedComponentsModule,
    NgxSpinnerModule,
    MegapayLoaderComponent,
    SharedPipeModule,
    PaginationComponent,
    TableControlsComponent,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    DropdownFieldComponent,
    DateInputComponent,
    MegapayLoaderComponent,
    NgxSpinnerModule,
    MatTooltip
  ]
})
export class RedeemComponent implements OnInit, OnDestroy {
  showTable = true;
  private destroy$ = new Subject<void>();

  tabs = TRANSACTION_TAB_OPTIONS;
  public form: UntypedFormGroup;
  searchForm: UntypedFormGroup;
  limitForm: UntypedFormGroup;

  categoryOptions = [
    { label: 'Stores', value: 'STORES' },
    { label: 'Pins', value: 'PINS' },
    { label: 'Load', value: 'LOAD' },
  ];
  categoryType: any = null;
  categoryType_text: any = null;

  // Table
  tableSubHeader = REDEEM_TABLE_SUB_HEADERS;

  // Data
  redeem_lists: any[] = [];
  submitted = false;
  isLoading = false;
  fileName = '';

  // Filter fields - stores the submitted filter criteria
  filterFields: any = null;

  // Pagination
  size = 10;
  total_pages = 0;
  current_page = 1;
  pages_array: number[] = [];
  total = 0;
  
  // Search
  search_keyword = '';
  searchKey: any = null;
  limitKey: any = null;
  
  // Sorting
  sort_column = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  //RESET FORMS
  private resetForms(): void{
    this.form.reset({search: ''});
    this.searchForm.reset({search: ''});
    this.limitForm.reset({search: ''});
  }

  //RESET CATEGORIES
  private resetCategories(): void{
    this.categoryType = '';
    this.categoryType_text = '';
  }

  //RESET DATA
  private resetData(): void{
    this.redeem_lists = null;
    this.submitted = false;
    this.isLoading = false;
    this.fileName = '';
  }

  //RESET FILTER FIELDS
  private resetFilterFields(): void{
    this.filterFields = '';
  }

  //RESET PAGINATION
  private resetPagination(): void{
    this.size = 10;
    this.total_pages = 0;
    this.current_page = 1;
    this.pages_array =  null;
    this.total = 0;
  }

  //RESET SEARCH
  private resetSearch(): void{
    this.search_keyword = '';
    this.searchKey =  '';
    this.limitKey = '';
  }

  // RESET LOCAL STORAGE
  private resetLocalStorage(): void{
    localStorage.setItem('itemLists', JSON.stringify(null));
  }

  private resetTableState(): void{
    this.resetForms();
    this.resetCategories();
    this.resetData();
    this.resetFilterFields();
    this.resetPagination();
    this.resetSearch();
    this.resetLocalStorage();
  }

  constructor(
    private fb: UntypedFormBuilder,
    private request: RequestsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public apiService: ApiService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private refreshService: RefreshService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on init:', err);
    });

    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.form.reset({search: ''}); //reset filter fields
      this.resetTableState() //reset filters, pagination, entries, etc.
      this.showTable = false; 

    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on destroy:', err);
    });
  }

  initializeForm(): void {
    this.form = this.fb.group({
      transactionId: [null],
      firstName: [null],
      middleName: [null],
      lastName: [null],
      phoneNumber: [null],
      productCode: [null],
      dateFrom: [null],
      dateTo: [null],
      category: [null],
    });

    // Search with debounce
    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.search_keyword = this.searchForm.value.search;
      
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }, 500);

    this.searchForm = this.fb.group({
      search: ['']
    });

    // Limit with debounce
    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;
      
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }, 500);

    this.limitForm = this.fb.group({
      limit: [this.size]
    });
  }

  
  submit(): void {
    if (!this.validateDates()) {
      return;
    }
    
    this.isLoading = true;
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });


    this.current_page = 1;

    const params = {
      transactionId: this.form.controls.transactionId.value,
      firstName: this.form.controls.firstName.value,
      middleName: this.form.controls.middleName.value,
      lastName: this.form.controls.lastName.value,
      phoneNumber: this.form.controls.phoneNumber.value,
      productCode: this.form.controls.productCode.value,
      dateFrom: this.form.controls.dateFrom.value,
      dateTo: this.form.controls.dateTo.value,
      category: this.categoryType,
      sortBy: {
        [this.sort_column]: this.sort_type
      }
    };

    this.filterFields = params;
    localStorage.setItem('itemLists', JSON.stringify(params));
    this.pages_array = [];

    this.apiService.getRedeemTransactions(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {

          if (response.status === 200) {
            this.redeem_lists = response.result.data;
            this.submitted = true;

            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages
            );
            this.total_pages = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total = parseInt(response.result.totalItems.toString(), 10);
            
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Redeem Transactions'
          );
        },
        complete: () => {
          this.form.reset();
          this.categoryType = null;
          this.categoryType_text = '';
          
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        }
      });
    this.showTable = true; //shows table
  }

  get_filtered_list(): void {
    this.isLoading = true;
    
    const params = {
      ...this.filterFields,
      search: this.search_keyword,
      sortBy: {
        [this.sort_column]: this.sort_type
      }
    };

    localStorage.setItem('itemLists', JSON.stringify(params));
    this.pages_array = [];

    this.apiService.getRedeemTransactions(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.redeem_lists = response.result.data;
            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages
            );
            this.total_pages = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Redeem Transactions'
          );
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        }
      });
  }

  setCategory(text: string, value: string): void {
    this.categoryType = value;
    this.categoryType_text = text;  
  }

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList(): void {
    this.current_page = 1;
    this.search_keyword = this.searchForm.value.search;
    
    if (this.filterFields) {
      this.get_filtered_list();
    }
  }

  validateDates(): boolean {
    const dateFrom = this.form.get('dateFrom').value;
    const dateTo = this.form.get('dateTo').value;

    if (dateFrom && dateTo) {
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);

      // Reset time to compare only dates
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(0, 0, 0, 0);

      if (toDate < fromDate) {
        this.toastr.error('Date To cannot be earlier than Date From', 'Invalid Date Range');
        return false;
      }
    }
    return true;
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch((err) => {
      console.error('Spinner show sort error:', err);
    });

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type = type;
    
    if (this.submitted && this.filterFields) {
      this.get_filtered_list();
    }
  }

  setPage(num: number): void {
    this.current_page = num;
    
    if (this.filterFields) {
      this.get_filtered_list();
    }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }
  }

  async print(){
    this.spinner.show().catch(error => { console.log(error); });
    const current = new Date();
    this.filterFields = JSON.parse(localStorage.getItem('itemLists'));
    this.fileName = current.toLocaleDateString() + "-redeems.xlsx";

    try {
      const requestBlob = await this.request.postBlob(Urls.exportTransactionsRedeem, this.filterFields).then(report => {
        const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
        const data = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = data;
        link.download = this.fileName;
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      });
      this.spinner.hide().catch(error => { console.log(error); });
    } catch (error) {
      this.spinner.hide().catch(err => { console.log(err); });
    }
  }
}
